import { computed, ref, type ComputedRef, type Ref } from 'vue';

import { http } from '@/interceptors/axiosRequestInterceptor';
import type { MyReadingFeedback } from '@/composables/useReadingFeedback';

/**
 * Ask ELSA — order analysis (AB#21202).
 *
 * Two halves that fail independently, mirroring the backend. `measured` is
 * computed from the order's own timeline and is present on every response,
 * including the ones where the generated reading could not be produced.
 * Nothing in here may let a failure in the second half clear the first — the
 * band is the part that cannot be wrong, and blanking it because a model was
 * unreachable would throw away the useful half of the tab.
 */

export interface Claim {
  claim: string;
  evidence: string;
  /** AB#21732 — actions only: the control that carries it out (elsaActions.ts). */
  do?: string | null;
  /** AB#21732 — Y-m-d for set_in_hand_date / set_ship_date. */
  date?: string | null;
}

export interface MeasuredBand {
  stage: string;
  stage_label: string;
  days_in_stage: number;
  sample_size: number;
  has_comparison: boolean;
  /**
   * The order is in a final state (completed/cancelled). days_in_stage is then
   * time SINCE it finished, not time waiting, so there is no verdict and no
   * comparison — the elapsed time is shown for context only. Optional so a
   * cached payload from before this field still parses.
   */
  terminal?: boolean;
  /** null when there is not enough history to compare against, or the order is terminal. */
  verdict: 'good' | 'watch' | 'late' | null;
  typical_low: number | null;
  typical_high: number | null;
  late_from: number | null;
  scale_max: number | null;
  /** The date this order was promised for, or null if it has none. */
  promised_ship_date: string | null;
  /**
   * Where that promise falls on the same day axis as days_in_stage.
   *
   * Null both when there is no promise AND when there is one that sits past
   * scale_max — the band declines to draw a mark the axis cannot hold, because
   * it would pile up against the right edge and read as "due now".
   */
  promise_at_days: number | null;
  /** Negative once the promise is behind us. Derived from the two above. */
  days_until_promise: number | null;
}

export interface ElsaReading {
  status: 'green' | 'orange' | 'red';
  /** One line, the single thing to know; readings stored before it carry their first sentence. */
  headline?: string | null;
  summary: string;
  eta: { earliest: string | null; latest: string | null; basis: string | null };
  risks: Claim[];
  signals: Claim[];
  actions: Claim[];
  coverage: Record<string, number>;
  generated_at: string | null;
  model: string;
  /** Stage transitions since the reading was generated. */
  events_since: number;
  /**
   * AB#21582 — the automatic refresh's state for this order: current (nothing
   * pending), queued (a change landed; a refresh will run once it settles),
   * generating, or failed (the last automatic attempt failed; this reading is
   * the previous good one). `reason` is why the last regeneration happened.
   * Absent on a backend that predates auto-refresh.
   */
  /** AB#21909 — this person's verdict on this reading, if any. */
  my_feedback?: MyReadingFeedback | null;
  refresh?: {
    state: 'current' | 'queued' | 'generating' | 'failed';
    reason: string | null;
    failed_attempts: number;
    auto: boolean;
  };
}

interface AnalysisPayload {
  measured?: MeasuredBand;
  analysis?: ElsaReading | null;
  available?: boolean;
}

export interface UseOrderAnalysis {
  measured: Ref<MeasuredBand | null>;
  reading: Ref<ElsaReading | null>;
  available: Ref<boolean>;
  isLoading: Ref<boolean>;
  isGenerating: Ref<boolean>;
  error: Ref<string | null>;
  isStale: ComputedRef<boolean>;
  load: () => Promise<void>;
  generate: () => Promise<void>;
}

export const useOrderAnalysis = (orderItemId: number): UseOrderAnalysis => {
  const measured = ref<MeasuredBand | null>(null);
  const reading = ref<ElsaReading | null>(null);
  const available = ref(true);
  const isLoading = ref(false);
  const isGenerating = ref(false);
  const error = ref<string | null>(null);

  /**
   * A reading with events behind it is not wrong, but it must announce itself.
   * A confident summary of an order that has moved twice since is the failure
   * this feature has to avoid.
   */
  const isStale: ComputedRef<boolean> = computed(() => (reading.value?.events_since ?? 0) > 0);

  /** Take the measured band from every response, successful or not. */
  const absorb = (payload: AnalysisPayload): void => {
    if (payload.measured) {
      measured.value = payload.measured;
    }

    if (payload.analysis !== undefined) {
      reading.value = payload.analysis;
    }

    if (payload.available !== undefined) {
      available.value = payload.available;
    }
  };

  const load = async (): Promise<void> => {
    isLoading.value = true;
    error.value = null;

    try {
      const { data } = await http.get(`order-items/${orderItemId}/analysis`);
      absorb((data?.result ?? {}) as AnalysisPayload);

      if (data?.success !== true) {
        error.value = data?.message || 'The order analysis could not be loaded.';
      }
    } catch {
      // Deliberately not handleResponseException: a tab that failed to load is
      // not a page-level error, and a toast over the order screen would
      // overstate it.
      error.value = 'The order analysis could not be loaded.';
    } finally {
      isLoading.value = false;
    }
  };

  const generate = async (): Promise<void> => {
    isGenerating.value = true;
    error.value = null;

    try {
      const { data } = await http.post(`order-items/${orderItemId}/analysis`);
      // Absorbed before the success check on purpose: a handled failure still
      // carries the measured band, and the band is the half worth keeping.
      absorb((data?.result ?? {}) as AnalysisPayload);

      if (data?.success !== true) {
        // The backend's message on a handled failure is written to be shown
        // verbatim — the request was fine, the model was not.
        error.value = data?.message || 'ELSA could not read this order right now.';
      }
    } catch {
      error.value = 'ELSA could not read this order right now.';
    } finally {
      isGenerating.value = false;
    }
  };

  return { measured, reading, available, isLoading, isGenerating, error, isStale, load, generate };
};
